import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { ConfigService } from './config.service';
import { Log, IShortLogger } from './logger.service';
import {
  SharedConnectionRequest,
  SharedConnectionResponse,
  ActiveConnection
} from '../models/shared-connection.model';

/**
 * Service for managing shared Guacamole connections between students and trainers.
 *
 * Simplified implementation - backend stores and retrieves connection info directly.
 * No proxy API calls required.
 *
 * @class ConnectionSharingService
 */
@Injectable({
  providedIn: 'root'
})
export class ConnectionSharingService {
  private readonly logger: IShortLogger<ConnectionSharingService> = Log.create(ConnectionSharingService);

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) {}

  /**
   * Get all active connections (for trainer dashboard)
   * Note: This now queries backend storage, not the proxy API
   *
   * @deprecated This method may not be implemented in simplified version.
   * The backend should provide an endpoint to list active sessions if needed.
   * @returns Observable of active connections
   */
  getActiveConnections(): Observable<{ connections: ActiveConnection[], count: number }> {
    const url = `${this.configService.getApiUrl()}/guac-proxy-bridge/active-connections`;
    this.logger.d('Fetching active connections from backend:', url);
    return this.http.get<{ connections: ActiveConnection[], count: number }>(url);
  }

  /**
   * Get shared connection information for trainer to join student's session
   * Backend retrieves this from its own storage (connectionId stored during mark-as-active)
   *
   * @param request - Shared connection request
   * @returns Promise of shared connection response
   */
  async getSharedConnection(request: SharedConnectionRequest): Promise<SharedConnectionResponse> {
    const url = `${this.configService.getApiUrl()}/v1/classes/get-shared-connection`;
    this.logger.d('Requesting shared connection from backend:', request);

    try {
      const response = await firstValueFrom(
        this.http.post<SharedConnectionResponse>(url, request)
      );
      this.logger.d('Received shared connection response:', response);
      return response;
    } catch (error) {
      this.logger.e('Failed to get shared connection:', error);
      throw error;
    }
  }

  /**
   * Check if a student has an active connection
   *
   * @param classId - Class ID
   * @param studentUserId - Student's user ID
   * @returns Promise of boolean indicating if connection exists
   */
  async hasActiveConnection(classId: string, studentUserId: string): Promise<boolean> {
    try {
      // Simplified: Attempt to get shared connection to check if student is active
      // If successful, student is connected. If 404, student is not connected.
      const request: SharedConnectionRequest = {
        classId,
        studentUserId,
        trainerUserId: 'check' // Dummy trainer ID for check
      };
      await this.getSharedConnection(request);
      return true;
    } catch (error: any) {
      if (error?.status === 404) {
        return false;
      }
      this.logger.e('Error checking active connection:', error);
      return false;
    }
  }
}
